// Brand studio: app name, logos and colours with a live phone preview.
import { api } from "../lib/api";
import { applyBrand, brandVars, contrast, onColor, type BrandColors } from "../lib/brand";
import { fmt, t, tl } from "../lib/i18n";
import { h, local, resource } from "../lib/ui";
import { act, Button, Chip, confirmDialog, Field, isBusy, Loading, LoadError, toast } from "../shared/components";
import { Icon, Roofline } from "../shared/icons";
import { PageHead } from "./app";

interface BrandData {
  appName: string;
  shortName: string;
  tagline: { en: string; fr: string };
  logoMarkFile: string | null;
  logoWordmarkFile: string | null;
  colors: BrandColors;
  supportEmail: string;
  supportPhone: string;
  version: number;
}

const COLOR_GROUPS: { key: string; colors: (keyof BrandColors)[] }[] = [
  { key: "brand", colors: ["primary", "ink", "accent", "accentText", "soft"] },
  { key: "surfaces", colors: ["background", "muted", "line"] },
  { key: "status", colors: ["good", "attention", "critical"] },
];

const HEX = /^#[0-9a-f]{6}$/i;

const clone = <T,>(x: T): T => JSON.parse(JSON.stringify(x));

function DefaultMark(props: { colors: BrandColors; size: number }) {
  return (
    <svg width={props.size} height={props.size} viewBox="0 0 64 64" aria-hidden="true" style="border-radius:22%">
      <rect width="64" height="64" rx="14" fill={props.colors.primary} />
      <g transform="translate(6 5) scale(1.3)">
        <path d="M6 21 L20 9 L34 21" fill="none" stroke={props.colors.accent} stroke-width="4.5" stroke-linecap="round" stroke-linejoin="round" />
        <circle cx="20" cy="28" r="4.2" fill={props.colors.accent} />
      </g>
    </svg>
  );
}

function MarkView(props: { d: BrandData; size: number }) {
  return props.d.logoMarkFile ? (
    <img src={`/brand/file/${props.d.logoMarkFile}`} alt="" width={props.size} height={props.size} style="border-radius:22%;object-fit:contain" />
  ) : (
    <DefaultMark colors={props.d.colors} size={props.size} />
  );
}

/** A miniature of the customer home screen, painted with the draft brand. */
function PhonePreview(props: { d: BrandData }) {
  const d = props.d;
  const vars = brandVars(d.colors);
  return (
    <div class="phone" aria-label={t("admin.brand.previewLabel")}>
      <div class="phone-screen" style={vars}>
        <div style="display:flex;align-items:center;justify-content:space-between;padding:16px 16px 10px">
          <div style="display:flex;align-items:center;gap:8px">
            {d.logoWordmarkFile ? (
              <img src={`/brand/file/${d.logoWordmarkFile}`} alt="" style="height:22px;width:auto" />
            ) : (
              [<MarkView d={d} size={24} />, <span style="font-family:var(--font-display);font-weight:600;font-size:16px;letter-spacing:-.02em;color:var(--c-ink)">{d.appName || "—"}</span>]
            )}
          </div>
          <span style="font-size:11px;font-weight:600;color:var(--c-muted)">EN · FR</span>
        </div>
        <div style="padding:0 12px;display:grid;gap:10px;overflow:hidden">
          <div class="plate" style="padding:12px 16px 16px;border-radius:18px;gap:8px">
            <Roofline class="roofline" />
            <div class="where" style="font-size:11px">{t("admin.brand.sampleAddress")}</div>
            <h1 style="font-size:20px">{t("admin.brand.sampleStatus")}</h1>
            <div class="meta" style="font-size:11px">{t("admin.brand.sampleMeta")}</div>
          </div>
          <div class="decision" style="padding:12px;gap:8px;border-radius:14px">
            <div style="display:flex;justify-content:space-between;gap:8px;align-items:flex-start">
              <div>
                <div style="font-weight:600;color:var(--c-ink)">{t("admin.brand.sampleTicket")}</div>
                <div style="font-size:11px;color:var(--c-muted)">{t("admin.brand.sampleQuote")}</div>
              </div>
              <span class="amount" style="font-size:17px;color:var(--c-ink)">{fmt.money(18000)}</span>
            </div>
            <div style="display:flex;gap:6px">
              <span class="btn small" style="flex:1">{t("admin.brand.sampleApprove")}</span>
              <span class="btn small secondary">{t("admin.brand.sampleDetails")}</span>
            </div>
          </div>
          <div class="list" style="border-radius:14px">
            {[
              ["good", t("admin.brand.sampleVisit")],
              ["attention", t("admin.brand.sampleHeater")],
              ["critical", t("admin.brand.sampleLeak")],
            ].map(([k, label]) => (
              <div class="list-row" style="padding:10px 12px;gap:8px">
                <span class="grow" style="color:var(--c-ink);font-size:12px">{label}</span>
                <span class={`chip ${k}`} style="font-size:10px">{t(`health.${k}`)}</span>
              </div>
            ))}
          </div>
          <div style="font-size:11px;color:var(--c-accent-text);font-weight:600;text-align:center">{tl(d.tagline)}</div>
        </div>
      </div>
    </div>
  );
}

function ColorSwatch(props: { k: keyof BrandColors; d: BrandData; onChange: (k: keyof BrandColors, v: string) => void; defaults: BrandColors }) {
  const val = props.d.colors[props.k];
  const changed = val.toUpperCase() !== props.defaults[props.k].toUpperCase();
  return (
    <div class="swatch">
      <input type="color" value={val.toLowerCase()} aria-label={t(`admin.brand.colors.${props.k}`)}
        onInput={(e: Event) => props.onChange(props.k, (e.target as HTMLInputElement).value.toUpperCase())} />
      <div class="stack tight grow" style="gap:4px">
        <div class="row between" style="gap:6px">
          <span class="small strong">{t(`admin.brand.colors.${props.k}`)}</span>
          {changed ? (
            <button class="btn ghost small" style="min-height:22px;padding:0 6px;font-size:11px" title={t("admin.brand.useDefault")}
              onClick={() => props.onChange(props.k, props.defaults[props.k])}>{t("admin.brand.default")}</button>
          ) : null}
        </div>
        <input class="input" value={val} maxLength={7} spellcheck={false} aria-label={t("admin.brand.hexOf", { name: t(`admin.brand.colors.${props.k}`) })}
          onInput={(e: Event) => {
            let v = (e.target as HTMLInputElement).value.trim();
            if (!v.startsWith("#")) v = "#" + v;
            if (HEX.test(v)) props.onChange(props.k, v.toUpperCase());
          }} />
        <span class="xs muted">{t(`admin.brand.colorUse.${props.k}`)}</span>
      </div>
    </div>
  );
}

function contrastChecks(c: BrandColors) {
  return [
    { key: "ink_on_background", ratio: contrast(c.ink, c.background), min: 4.5 },
    { key: "muted_on_background", ratio: contrast(c.muted, c.background), min: 4.5 },
    { key: "on_primary", ratio: contrast(onColor(c.primary, c.ink), c.primary), min: 4.5 },
    { key: "accent_text_on_background", ratio: contrast(c.accentText, c.background), min: 4.5 },
    { key: "primary_on_background", ratio: contrast(c.primary, c.background), min: 3 },
    { key: "critical_on_white", ratio: contrast(c.critical, "#FFFFFF"), min: 4.5 },
  ];
}

function LogoSlot(props: { slot: "mark" | "wordmark"; d: BrandData; onFile: (id: string | null) => void }) {
  const id = props.slot === "mark" ? props.d.logoMarkFile : props.d.logoWordmarkFile;
  const busyKey = `brand.upload.${props.slot}`;
  const upload = (e: Event) => {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("slot", props.slot);
    fd.append("file", file);
    act(busyKey, async () => {
      const r = await api.upload("/api/admin/brand/logo", fd);
      props.onFile(r.file.id);
    });
  };
  return (
    <div class="logo-drop">
      <div class={`thumb ${props.slot === "wordmark" ? "wide" : ""}`}>
        {id ? <img src={`/brand/file/${id}`} alt="" /> : props.slot === "mark" ? <DefaultMark colors={props.d.colors} size={64} /> : <span class="xs muted">{t("admin.brand.none")}</span>}
      </div>
      <div class="stack tight grow">
        <span class="strong">{t(`admin.brand.${props.slot}Title`)}</span>
        <span class="xs muted">{t(`admin.brand.${props.slot}Hint`)}</span>
        <div class="row" style="gap:8px">
          <label class="btn secondary small" style="cursor:pointer">
            {isBusy(busyKey) ? <span class="spin" /> : <Icon name="upload" size={16} />}
            {t("admin.brand.upload")}
            <input type="file" accept="image/png,image/svg+xml,image/webp,image/jpeg" class="sr-only" onChange={upload} />
          </label>
          {id ? <Button kind="ghost" size="small" onClick={() => props.onFile(null)}>{props.slot === "mark" ? t("admin.brand.useDefaultMark") : t("admin.brand.remove")}</Button> : null}
        </div>
      </div>
    </div>
  );
}

export function BrandStudio() {
  const r = resource("admin.brand", () => api.get("/api/admin/brand"));
  const s = local("screen:brand", () => ({ draft: null as BrandData | null }));
  if (r.error && !r.data) return <LoadError error={r.error} retry={() => r.reload()} />;
  if (!r.data) return <Loading rows={8} />;
  const saved: BrandData = r.data.brand;
  const defaults: BrandData = r.data.defaults;
  const d: BrandData = s.draft ?? clone(saved);
  const dirty = JSON.stringify(d) !== JSON.stringify(saved);
  const edit = (fn: (x: BrandData) => void) => {
    const next = clone(d);
    fn(next);
    s.set({ draft: next });
  };
  const setColor = (k: keyof BrandColors, v: string) => edit((x) => (x.colors[k] = v));
  const checks = contrastChecks(d.colors);
  const failing = checks.filter((c) => c.ratio < c.min).length;

  const save = () =>
    act("brand.save", async () => {
      const body = { appName: d.appName, shortName: d.shortName, tagline: d.tagline, supportEmail: d.supportEmail, supportPhone: d.supportPhone, colors: d.colors, logoMarkFile: d.logoMarkFile, logoWordmarkFile: d.logoWordmarkFile };
      const res = await api.put("/api/admin/brand", body);
      r.data = { ...r.data, brand: res.brand, contrast: res.contrast, public: res.public };
      s.set({ draft: null });
      applyBrand(res.public);
      toast(t("admin.brand.saved"));
    });

  const reset = async () => {
    if (!(await confirmDialog({ title: t("admin.brand.resetTitle"), body: t("admin.brand.resetBody", { name: defaults.appName }), confirm: t("admin.brand.resetConfirm") }))) return;
    s.set({ draft: { ...clone(defaults), version: saved.version } });
  };

  const textField = (label: string, value: string, set: (v: string) => void, opts: { max?: number; hint?: string; type?: string; id: string }) => (
    <Field label={label} id={opts.id} hint={opts.hint}>
      <input class="input" id={opts.id} type={opts.type ?? "text"} value={value} maxLength={opts.max} onInput={(e: Event) => set((e.target as HTMLInputElement).value)} />
    </Field>
  );

  return (
    <div class="stack loose">
      <PageHead title={t("admin.brand.title")} intro={t("admin.brand.intro")}
        actions={<Button kind="secondary" onClick={reset}>{t("admin.brand.resetButton", { name: defaults.appName })}</Button>} />
      <div class="studio">
        <div class="stack loose">
          <section class="panel">
            <div class="panel-head"><h2>{t("admin.brand.identity")}</h2></div>
            <div class="panel-body stack">
              <div class="cols-2">
                {textField(t("admin.brand.appName"), d.appName, (v) => edit((x) => (x.appName = v)), { max: 40, id: "b-name", hint: t("admin.brand.appNameHint") })}
                {textField(t("admin.brand.shortName"), d.shortName, (v) => edit((x) => (x.shortName = v)), { max: 16, id: "b-short", hint: t("admin.brand.shortNameHint") })}
              </div>
              <div class="cols-2">
                {textField(t("admin.brand.taglineEn"), d.tagline.en, (v) => edit((x) => (x.tagline.en = v)), { max: 80, id: "b-tag-en" })}
                {textField(t("admin.brand.taglineFr"), d.tagline.fr, (v) => edit((x) => (x.tagline.fr = v)), { max: 80, id: "b-tag-fr" })}
              </div>
              <div class="cols-2">
                {textField(t("admin.brand.supportEmail"), d.supportEmail, (v) => edit((x) => (x.supportEmail = v)), { max: 120, id: "b-mail", type: "email" })}
                {textField(t("admin.brand.supportPhone"), d.supportPhone, (v) => edit((x) => (x.supportPhone = v)), { max: 30, id: "b-phone", type: "tel" })}
              </div>
            </div>
          </section>

          <section class="panel">
            <div class="panel-head"><h2>{t("admin.brand.logos")}</h2></div>
            <div class="panel-body stack">
              <LogoSlot slot="mark" d={d} onFile={(id) => edit((x) => (x.logoMarkFile = id))} />
              <LogoSlot slot="wordmark" d={d} onFile={(id) => edit((x) => (x.logoWordmarkFile = id))} />
            </div>
          </section>

          <section class="panel">
            <div class="panel-head"><h2>{t("admin.brand.colorsTitle")}</h2></div>
            <div class="panel-body stack loose">
              {COLOR_GROUPS.map((g) => (
                <div class="stack tight">
                  <h3 class="small" style="font-family:var(--font)">{t(`admin.brand.groups.${g.key}`)}</h3>
                  <div class="swatches">
                    {g.colors.map((k) => <ColorSwatch k={k} d={d} onChange={setColor} defaults={defaults.colors} />)}
                  </div>
                </div>
              ))}
              <div class="stack tight">
                <div class="row between">
                  <h3 class="small" style="font-family:var(--font)">{t("admin.brand.readability")}</h3>
                  {failing ? <Chip kind="attention">{t("admin.brand.checksFailing", { count: failing })}</Chip> : <Chip kind="good">{t("admin.brand.checksPass")}</Chip>}
                </div>
                <div class="contrast-list">
                  {checks.map((c) => (
                    <div>
                      <span>{t(`admin.brand.contrast.${c.key}`)}</span>
                      <span class="row" style="gap:8px">
                        <span class="tabular muted">{fmt.number(c.ratio, 2)}:1</span>
                        <Chip kind={c.ratio >= c.min ? "good" : "attention"} plain>{c.ratio >= c.min ? t("admin.brand.pass") : t("admin.brand.low", { min: c.min })}</Chip>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
        </div>

        <aside class="studio-preview">
          <PhonePreview d={d} />
          <p class="xs muted" style="max-width:320px;text-align:center">{t("admin.brand.previewNote")}</p>
        </aside>
      </div>

      {dirty ? (
        <div class="savebar" role="region" aria-label={t("common.unsaved")}>
          <span>{failing ? t("admin.brand.unsavedWithWarnings", { count: failing }) : t("admin.brand.unsaved")}</span>
          <div class="row">
            <button class="btn ghost" style="color:inherit" onClick={() => s.set({ draft: null })}>{t("common.discard")}</button>
            <Button kind="accent" busy={isBusy("brand.save")} onClick={save}>{t("admin.brand.publish")}</Button>
          </div>
        </div>
      ) : (
        <p class="xs muted">{t("admin.brand.version", { v: saved.version })}</p>
      )}
    </div>
  );
}

